import React from 'react';
import { PitchDeckSlide } from '../../types/pitchDeck';

interface SlideListProps {
  slides: PitchDeckSlide[];
  selectedSlideId: string | null;
  onSelectSlide: (slideId: string | null) => void;
  onReorderSlides: (slides: PitchDeckSlide[]) => void;
  onRemoveSlide: (slideId: string) => Promise<void>;
}

const SlideList: React.FC<SlideListProps> = ({
  slides,
  selectedSlideId,
  onSelectSlide,
  onReorderSlides,
  onRemoveSlide,
}) => {
  // Handle drag and drop reordering
  const [draggedSlideId, setDraggedSlideId] = React.useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, slideId: string) => {
    setDraggedSlideId(slideId);
    e.dataTransfer.setData('text/plain', slideId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, targetSlideId: string) => {
    e.preventDefault();
    
    if (!draggedSlideId || draggedSlideId === targetSlideId) return;
    
    const draggedIndex = slides.findIndex(slide => slide.id === draggedSlideId);
    const targetIndex = slides.findIndex(slide => slide.id === targetSlideId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Create new slide order
    const newSlides = [...slides];
    const [draggedSlide] = newSlides.splice(draggedIndex, 1);
    newSlides.splice(targetIndex, 0, draggedSlide);
    
    // Update order in store
    onReorderSlides(newSlides);
    
    setDraggedSlideId(null);
  };

  const handleDragEnd = () => {
    setDraggedSlideId(null);
  };

  return (
    <div className="py-2">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`p-2 mb-2 border rounded cursor-pointer ${
            selectedSlideId === slide.id
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:bg-gray-100'
          } ${draggedSlideId === slide.id ? 'opacity-50' : ''}`}
          onClick={() => onSelectSlide(slide.id)}
          draggable
          onDragStart={(e) => handleDragStart(e, slide.id)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, slide.id)}
          onDragEnd={handleDragEnd}
        >
          <div className="flex items-center">
            <div className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded mr-2">
              {index + 1}
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="text-sm font-medium truncate">{slide.title}</div>
              <div className="text-xs text-gray-500">{slide.type}</div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemoveSlide(slide.id);
              }}
              className="text-gray-400 hover:text-red-600 ml-2"
            >
              <span className="text-lg">&times;</span>
            </button>
          </div>
        </div>
      ))}
      
      {slides.length === 0 && (
        <div className="p-4 text-center text-gray-500">
          <p>No slides yet</p>
          <p className="text-xs">Click + to add slides</p>
        </div>
      )}
    </div>
  );
};

export default SlideList;
